/**
 * TimelineChartContainer Component
 *
 * Orchestrates the timeline chart with timeframe selector and comparison features.
 * Wraps TimelineChart with additional controls and state management.
 */

import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from 'lucide-react';
import type { HistoricalPeriod } from '../../types';
import type { TimeframeOption, ChartSeries, LegendChip, HoverInfo } from './types';
import TimelineChart from './TimelineChart';
import TimeframeSelector from './TimeframeSelector';
import CompareDropdown from './CompareDropdown';
import LegendChips from './LegendChips';
import { useComparisonHistory } from '../../../../hooks/useComparisonHistory';
import { shortenInstitutionName } from '../../../../utils/institutionNames';
import styles from './TimelineChart.module.css';

// Series colors from CSS variables
const SERIES_COLORS = [
  'var(--chart-series-1)',
  'var(--chart-series-2)',
  'var(--chart-series-3)',
  'var(--chart-series-4)',
];

interface TimelineChartContainerProps {
  /** Historical rating data */
  history: HistoricalPeriod[];
  /** Institution info */
  institution: { id: string; name: string };
  /** Currently selected period index */
  selectedIndex: number;
  /** Callback when a period is selected */
  onSelectPeriod: (index: number) => void;
}

/**
 * Filter history by timeframe
 * Calculates cutoff relative to the LATEST period in the data (not today's date)
 */
function filterByTimeframe(
  history: HistoricalPeriod[],
  timeframe: TimeframeOption
): HistoricalPeriod[] {
  if (timeframe === 'MAX' || history.length === 0) return history;

  const months: Record<Exclude<TimeframeOption, 'MAX'>, number> = {
    '6M': 6,
    '1Y': 12,
    '2Y': 24,
    '3Y': 36,
  };

  // Get the latest period date from the data (history is sorted descending, so index 0 is latest)
  const latestPeriod = history[0];
  const [latestYear, latestMonth] = latestPeriod.date.split('-').map(Number);
  const latestDate = new Date(latestYear, latestMonth - 1, 1);

  // Calculate cutoff relative to latest period, not today
  const cutoffMonths = months[timeframe];
  const cutoff = new Date(latestDate);
  cutoff.setMonth(cutoff.getMonth() - cutoffMonths);

  return history.filter((h) => {
    // Dates are in YYYY-MM format
    const [year, month] = h.date.split('-').map(Number);
    const periodDate = new Date(year, month - 1, 1);
    return periodDate >= cutoff;
  });
}

/**
 * Calculate rating change (score difference between latest and previous period)
 */
function calculateRatingChange(history: HistoricalPeriod[]): string | null {
  if (history.length < 2) return null;
  const latest = history[0].finalScore;
  const previous = history[1].finalScore;
  const diff = latest - previous;
  return diff > 0 ? `+${diff.toFixed(1)}` : diff.toFixed(1);
}

export default function TimelineChartContainer({
  history,
  institution,
  selectedIndex,
  onSelectPeriod,
}: TimelineChartContainerProps) {
  const { t } = useTranslation();
  const [timeframe, setTimeframe] = useState<TimeframeOption>('2Y');
  const [highlightedSeries, setHighlightedSeries] = useState<string | null>(null);
  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);
  const [comparisonInstitutions, setComparisonInstitutions] = useState<
    Array<{ id: string; name: string }>
  >([]);

  // Fetch comparison history data
  const { data: comparisonData, fetchHistory, removeHistory } = useComparisonHistory();

  // Filter history based on timeframe
  const filteredHistory = useMemo(
    () => filterByTimeframe(history, timeframe),
    [history, timeframe]
  );

  // Handle adding comparison institution
  const handleAddComparison = useCallback(
    (inst: { id: string; name: string; rating?: string }) => {
      setComparisonInstitutions((prev) => [...prev, { id: inst.id, name: inst.name }]);
      fetchHistory(inst.id, inst.name);
    },
    [fetchHistory]
  );

  // Handle removing comparison institution
  const handleRemoveComparison = useCallback(
    (institutionId: string) => {
      setComparisonInstitutions((prev) => prev.filter((i) => i.id !== institutionId));
      removeHistory(institutionId);
    },
    [removeHistory]
  );

  // Build series array for multi-line chart
  const chartSeries: ChartSeries[] = useMemo(() => {
    const series: ChartSeries[] = [];

    // Primary series (current institution)
    series.push({
      institutionId: institution.id,
      institutionName: institution.name,
      history: filterByTimeframe(history, timeframe),
      color: SERIES_COLORS[0],
      isPrimary: true,
    });

    // Comparison series
    comparisonInstitutions.forEach((comp, index) => {
      const data = comparisonData.get(comp.id);
      if (data && data.history.length > 0) {
        series.push({
          institutionId: comp.id,
          institutionName: comp.name,
          history: filterByTimeframe(data.history, timeframe),
          color: SERIES_COLORS[(index + 1) % SERIES_COLORS.length],
          isPrimary: false,
        });
      }
    });

    return series;
  }, [institution, history, timeframe, comparisonInstitutions, comparisonData]);

  // Build legend chips from series data, with hover values when crosshair active
  const legendChips: LegendChip[] = useMemo(() => {
    return chartSeries.map((s) => {
      const latestPeriod = s.history[0];
      // Check if we have hover data for this institution
      const hoverValue = hoverInfo?.values.find((v) => v.institutionId === s.institutionId);

      return {
        institutionId: s.institutionId,
        name: shortenInstitutionName(s.institutionName),
        rating: latestPeriod?.rating || '—',
        ratingChange: calculateRatingChange(s.history),
        color: s.color,
        isPrimary: s.isPrimary,
        // Add hover values if crosshair is active
        hoverRating: hoverValue?.rating,
        hoverScore: hoverValue?.score,
      };
    });
  }, [chartSeries, hoverInfo]);

  // Adjust selected index when filtering changes the available data
  const adjustedSelectedIndex = useMemo(() => {
    if (filteredHistory.length === 0) return 0;
    // If selected period is not in filtered data, select the latest
    const selectedPeriod = history[selectedIndex];
    if (!selectedPeriod) return 0;

    const filteredIndex = filteredHistory.findIndex(
      (h) => h.date === selectedPeriod.date
    );
    return filteredIndex >= 0 ? filteredIndex : 0;
  }, [history, selectedIndex, filteredHistory]);

  // Handle period selection - map back to original history index
  const handleSelectPeriod = (filteredIndex: number) => {
    const selectedPeriod = filteredHistory[filteredIndex];
    if (!selectedPeriod) return;

    const originalIndex = history.findIndex((h) => h.date === selectedPeriod.date);
    if (originalIndex >= 0) {
      onSelectPeriod(originalIndex);
    }
  };

  return (
    <div className={styles.chartContainer}>
      <div className={styles.containerHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.timelineTitle}>
            <Calendar size={16} aria-hidden="true" />
            <span>{t('timelineChart.ratingTimeline')}</span>
          </div>
          <TimeframeSelector value={timeframe} onChange={setTimeframe} />
        </div>
        <div className={styles.headerRight}>
          <CompareDropdown
            selectedInstitutions={comparisonInstitutions}
            onAdd={handleAddComparison}
            onRemove={handleRemoveComparison}
            excludeId={institution.id}
            maxCompare={3}
          />
        </div>
      </div>

      <TimelineChart
        series={chartSeries}
        selectedIndex={adjustedSelectedIndex}
        onSelectPeriod={handleSelectPeriod}
        hideHeader
        highlightedSeries={highlightedSeries}
        onHoverPoint={setHoverInfo}
      />

      {legendChips.length > 0 && (
        <LegendChips
          chips={legendChips}
          highlightedId={highlightedSeries}
          onHover={setHighlightedSeries}
          onRemove={handleRemoveComparison}
        />
      )}
    </div>
  );
}
